// Types
import type { Communication } from "@medplum/fhirtypes";
import {
  isPhoneCallCommunication,
  isSmsCommunication,
} from "@benchmark/core/resolvers";
import type {
  CallDirection,
  CallState,
  CallMetadata,
} from "@/types/domain/messaging";

/** Extension URLs for call metadata in FHIR Communication resources. @agentic @benchmark/core */
const CALL_EXTENSION_URLS = {
  duration:
    "https://benchmark.health/fhir/StructureDefinition/call-duration-ms",
  direction: "https://benchmark.health/fhir/StructureDefinition/call-direction",
  state: "https://benchmark.health/fhir/StructureDefinition/call-state",
  dialpadCallId:
    "https://benchmark.health/fhir/StructureDefinition/dialpad-call-id",
} as const;

/**
 * Extract call duration in milliseconds from Communication extensions.
 * @agentic @benchmark/core
 */
export function getCallDuration(
  communication: Communication,
): number | undefined {
  const extension = communication.extension?.find(
    (ext) => ext.url === CALL_EXTENSION_URLS.duration,
  );
  return extension?.valueInteger;
}

/**
 * Extract call direction from Communication extensions.
 * @agentic @benchmark/core
 */
export function getCallDirection(
  communication: Communication,
): CallDirection | undefined {
  const extension = communication.extension?.find(
    (ext) => ext.url === CALL_EXTENSION_URLS.direction,
  );
  const value = extension?.valueCode;
  if (value === "inbound" || value === "outbound") {
    return value;
  }
  return undefined;
}

/**
 * Extract call state from Communication extensions.
 * @agentic @benchmark/core
 */
export function getCallState(
  communication: Communication,
): CallState | undefined {
  const extension = communication.extension?.find(
    (ext) => ext.url === CALL_EXTENSION_URLS.state,
  );
  const value = extension?.valueCode;
  if (
    value === "completed" ||
    value === "missed" ||
    value === "voicemail" ||
    value === "in-progress"
  ) {
    return value;
  }
  return undefined;
}

/**
 * Extract Dialpad call ID from Communication extensions. Records written
 * before the 32-bit FHIR integer range was enforced carry `valueInteger`.
 * @agentic @benchmark/core
 */
export function getDialpadCallId(
  communication: Communication,
): string | undefined {
  const extension = communication.extension?.find(
    (ext) => ext.url === CALL_EXTENSION_URLS.dialpadCallId,
  );
  return extension?.valueString ?? extension?.valueInteger?.toString();
}

/**
 * Extract all call metadata from Communication extensions.
 * @agentic @benchmark/core
 */
export function getCallMetadata(communication: Communication): CallMetadata {
  return {
    durationMs: getCallDuration(communication),
    direction: getCallDirection(communication),
    state: getCallState(communication),
    dialpadCallId: getDialpadCallId(communication),
  };
}

/**
 * Format a call date for display. Shows "Today" or "Yesterday" for recent dates, otherwise shows the short date. Always includes the time.
 * @agentic @benchmark/ui
 */
export function formatCallDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (dateOnly.getTime() === today.getTime()) {
    return `Today, ${timeStr}`;
  }

  if (dateOnly.getTime() === yesterday.getTime()) {
    return `Yesterday, ${timeStr}`;
  }

  const dateStr = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${dateStr}, ${timeStr}`;
}

/**
 * Format call duration for display (e.g., "30 sec", "3 min", "1 hr 30 min").
 * @agentic @benchmark/ui
 */
export function formatCallDuration(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} hr`);
  }

  if (minutes > 0) {
    parts.push(`${minutes} min`);
  }

  // Only show seconds if duration is less than a minute
  if (hours === 0 && minutes === 0 && seconds > 0) {
    parts.push(`${seconds} sec`);
  }

  return parts.length > 0 ? parts.join(" ") : "0 sec";
}

/**
 * Check if a Communication is a phone call (vs SMS). Delegates to the shared
 * core resolver, which recognizes both the canonical (`medium` channel +
 * `call-state` category) and legacy (`communication-channel`) encodings.
 * @agentic connections
 */
export function isPhoneCall(communication: Communication): boolean {
  return isPhoneCallCommunication(communication);
}

/**
 * Check if a Communication is an SMS message. Delegates to the shared core
 * resolver (canonical `medium=text` + `patient-message`, or legacy
 * `communication-channel|sms`).
 * @agentic connections
 */
export function isSMSMessage(communication: Communication): boolean {
  return isSmsCommunication(communication);
}

/**
 * Get the transcript text from a call Communication.
 * @agentic @benchmark/core
 */
export function getCallTranscript(
  communication: Communication,
): string | undefined {
  return communication.payload?.[0]?.contentString;
}
